import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  DialogActions,
  TextField,
  Typography,
} from "@mui/material";
import ModalBasicLayout from "../ModalBasicLayout";
import ContainerModalActions from "../../Containers/ContainerModalActions";
import { useTranslationApp } from "../../../lib/i18next";
import { useDispatchApp } from "../../../lib/redux";
import {
  BUTTON_CANCEL,
  BUTTON_DUPLICATE,
  BUTTON_GO_BACK,
  DUPLICATE_AD_OTHER_GROUP_AD_GROUP_LABEL,
  DUPLICATE_AD_OTHER_GROUP_AD_GROUP_PLACEHOLDER,
  DUPLICATE_AD_OTHER_GROUP_CAMPAIGN_LABEL,
  DUPLICATE_AD_OTHER_GROUP_CAMPAIGN_CHANGE_WARNING,
  DUPLICATE_AD_OTHER_GROUP_CAMPAIGN_PLACEHOLDER,
  DUPLICATE_AD_OTHER_GROUP_EMPTY_AD_GROUPS,
  DUPLICATE_AD_OTHER_GROUP_MODAL_DESCRIPTION,
  DUPLICATE_AD_OTHER_GROUP_MODAL_TITLE,
  WAIT,
} from "../../../i18n/keysTranslations";
import {
  startGetAdsCampaign,
  startGetAdsCampaigns,
} from "../../../actions/adsCampaigns";
import { DEFAULT_SORT_CONFIG } from "../../../utils/constants";
import { INPUT_COLOR_BORDER } from "../../../utils/colors";
import { ConfirmAlert } from "../../Alerts/Alerts";

const SCROLL_BOTTOM_THRESHOLD = 20;
const CAMPAIGNS_PAGE_SIZE = 20;
const AUTOCOMPLETE_PAPER_SX = {
  border: `1px solid ${INPUT_COLOR_BORDER}`,
  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
};
const AUTOCOMPLETE_TEXT_FIELD_SX = {
  backgroundColor: "fields.background",
  border: `1px solid ${INPUT_COLOR_BORDER}`,
  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
};

const mapCampaignToOption = ({ campaignID, campaign }) => {
  const campaignName =
    campaign?.platformsData?.[campaign?.platforms?.[0]]?.name ||
    campaign?.name ||
    campaignID;

  return {
    id: campaignID,
    name: campaignName,
  };
};

const mapAdGroupsToOptions = ({ adGroups = [], platform }) => {
  return (adGroups || [])
    .filter((adGroup) => adGroup?.platform === platform)
    .map((adGroup) => ({
      id: adGroup.id,
      name: adGroup.name || adGroup.id,
    }));
};

const getMergedCampaignOptions = ({
  defaultCampaignOption,
  previousOptions,
  newOptions,
}) => {
  const optionsMap = new Map();

  [defaultCampaignOption, ...previousOptions, ...newOptions].forEach((item) => {
    if (item?.id) {
      optionsMap.set(item.id, item);
    }
  });

  return Array.from(optionsMap.values());
};

const ModalDuplicateAdToAdGroup = ({
  modalOpen,
  onCloseModal = () => {},
  currentCampaignID,
  currentCampaignName,
  platform,
  currentCampaignAdGroups = [],
  onConfirmDuplicate = async () => false,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  const [campaignOptions, setCampaignOptions] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [adGroupOptions, setAdGroupOptions] = useState([]);
  const [selectedAdGroup, setSelectedAdGroup] = useState(null);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [isLoadingAdGroups, setIsLoadingAdGroups] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasMoreCampaigns, setHasMoreCampaigns] = useState(true);
  const [lastCampaignsKey, setLastCampaignsKey] = useState(null);
  const [campaignDraftByID, setCampaignDraftByID] = useState({});

  const isDuplicatingIntoAnotherCampaign =
    !!selectedCampaign?.id &&
    !!currentCampaignID &&
    selectedCampaign.id !== currentCampaignID;

  const defaultCampaignOption = useMemo(
    () => ({
      id: currentCampaignID,
      name: currentCampaignName || currentCampaignID,
    }),
    [currentCampaignID, currentCampaignName]
  );

  const onResetState = () => {
    setCampaignOptions([]);
    setSelectedCampaign(defaultCampaignOption);
    setAdGroupOptions([]);
    setSelectedAdGroup(null);
    setIsLoadingCampaigns(false);
    setIsLoadingAdGroups(false);
    setIsSubmitting(false);
    setHasMoreCampaigns(true);
    setLastCampaignsKey(null);
    setCampaignDraftByID({});
  };

  const onLoadCampaigns = async () => {
    if (isLoadingCampaigns || !hasMoreCampaigns) return;
    setIsLoadingCampaigns(true);

    try {
      const response = await dispatch(
        startGetAdsCampaigns({
          lastKey: lastCampaignsKey,
          limit: CAMPAIGNS_PAGE_SIZE,
          sortConfig: DEFAULT_SORT_CONFIG,
          onChangeLastKey: setLastCampaignsKey,
          onLoadLastKey: () => {
            setHasMoreCampaigns(false);
          },
        })
      );

      if (response && typeof response === "object") {
        const newOptions = Object.entries(response)
          .filter(([, campaign]) => campaign?.platforms?.includes(platform))
          .map(([campaignID, campaign]) =>
            mapCampaignToOption({ campaignID, campaign })
          );

        setCampaignOptions((previousOptions) =>
          getMergedCampaignOptions({
            defaultCampaignOption,
            previousOptions,
            newOptions,
          })
        );
      }
    } finally {
      setIsLoadingCampaigns(false);
    }
  };

  const onLoadAdGroupsFromCampaign = async (campaignOption) => {
    if (!campaignOption?.id) {
      setAdGroupOptions([]);
      setSelectedAdGroup(null);
      return;
    }

    setIsLoadingAdGroups(true);
    setSelectedAdGroup(null);

    if (
      campaignOption.id === currentCampaignID &&
      Array.isArray(currentCampaignAdGroups)
    ) {
      const currentOptions = mapAdGroupsToOptions({
        adGroups: currentCampaignAdGroups,
        platform,
      });
      setAdGroupOptions(currentOptions);
      setIsLoadingAdGroups(false);
      return;
    }

    const campaignFromCache = campaignDraftByID[campaignOption.id];
    const campaign =
      campaignFromCache ||
      (await dispatch(startGetAdsCampaign(campaignOption.id, true)));

    if (!campaign) {
      setAdGroupOptions([]);
      setIsLoadingAdGroups(false);
      return;
    }

    setCampaignDraftByID((prev) => ({
      ...prev,
      [campaignOption.id]: campaign,
    }));

    const options = mapAdGroupsToOptions({
      adGroups: campaign?.adsGroups || [],
      platform,
    });

    setAdGroupOptions(options);
    setIsLoadingAdGroups(false);
  };

  const onClose = () => {
    onCloseModal();
  };

  const onSubmit = async () => {
    if (!selectedCampaign?.id || !selectedAdGroup?.id || isSubmitting) return;

    if (isDuplicatingIntoAnotherCampaign) {
      const result = await ConfirmAlert({
        title: t(WAIT),
        text: t(DUPLICATE_AD_OTHER_GROUP_CAMPAIGN_CHANGE_WARNING),
        confirmButtonText: t(BUTTON_DUPLICATE),
        cancelButtonText: t(BUTTON_GO_BACK),
      });
      if (!result.isConfirmed) {
        onClose();
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const ok = await onConfirmDuplicate({
        targetCampaignID: selectedCampaign.id,
        targetAdGroupID: selectedAdGroup.id,
      });

      if (ok) {
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const onCampaignsListScroll = (event) => {
    const listboxNode = event.currentTarget;
    const isBottomReached =
      listboxNode.scrollTop + listboxNode.clientHeight >=
      listboxNode.scrollHeight - SCROLL_BOTTOM_THRESHOLD;

    if (isBottomReached && hasMoreCampaigns && !isLoadingCampaigns) {
      onLoadCampaigns();
    }
  };

  useEffect(() => {
    if (!modalOpen) return;
    onResetState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  useEffect(() => {
    if (!modalOpen) return;
    const onLoadInitial = async () => {
      setCampaignOptions([defaultCampaignOption]);
      setSelectedCampaign(defaultCampaignOption);
      await onLoadCampaigns();
    };

    onLoadInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, defaultCampaignOption.id, platform]);

  useEffect(() => {
    if (!modalOpen) return;
    onLoadAdGroupsFromCampaign(selectedCampaign);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaign, modalOpen]);

  const disableSubmit =
    isSubmitting ||
    isLoadingAdGroups ||
    !selectedCampaign?.id ||
    !selectedAdGroup?.id;

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onClose}
      title={t(DUPLICATE_AD_OTHER_GROUP_MODAL_TITLE)}
      description={t(DUPLICATE_AD_OTHER_GROUP_MODAL_DESCRIPTION)}
      maxWidth="sm"
      fullWidth
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          pt: 1,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body1" color="text.primary">
            {t(DUPLICATE_AD_OTHER_GROUP_CAMPAIGN_LABEL)}
          </Typography>
          <Autocomplete
            options={campaignOptions}
            value={selectedCampaign}
            loading={isLoadingCampaigns}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, value) => setSelectedCampaign(value)}
            ListboxProps={{
              onScroll: onCampaignsListScroll,
            }}
            PaperProps={{
              sx: AUTOCOMPLETE_PAPER_SX,
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                hiddenLabel={true}
                placeholder={t(DUPLICATE_AD_OTHER_GROUP_CAMPAIGN_PLACEHOLDER)}
                variant="filled"
                sx={AUTOCOMPLETE_TEXT_FIELD_SX}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingCampaigns ? (
                        <CircularProgress size={18} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="body1" color="text.primary">
            {t(DUPLICATE_AD_OTHER_GROUP_AD_GROUP_LABEL)}
          </Typography>
          <Autocomplete
            options={adGroupOptions}
            value={selectedAdGroup}
            loading={isLoadingAdGroups}
            disabled={!selectedCampaign?.id}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, value) => setSelectedAdGroup(value)}
            noOptionsText={t(DUPLICATE_AD_OTHER_GROUP_EMPTY_AD_GROUPS)}
            PaperProps={{
              sx: AUTOCOMPLETE_PAPER_SX,
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                hiddenLabel={true}
                placeholder={t(DUPLICATE_AD_OTHER_GROUP_AD_GROUP_PLACEHOLDER)}
                variant="filled"
                sx={AUTOCOMPLETE_TEXT_FIELD_SX}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingAdGroups ? (
                        <CircularProgress size={18} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>

        {!selectedCampaign?.id && (
          <Typography variant="caption" color="error.main">
            {t(DUPLICATE_AD_OTHER_GROUP_CAMPAIGN_PLACEHOLDER)}
          </Typography>
        )}
      </Box>

      <DialogActions>
        <ContainerModalActions
          propsPrimaryButton={{
            showButton: true,
            onClick: onSubmit,
            text: BUTTON_DUPLICATE,
            disabled: disableSubmit,
            loading: isSubmitting,
          }}
          propsSecondaryButton={{
            showButton: true,
            onClick: onClose,
            text: BUTTON_CANCEL,
            disabled: isSubmitting,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default ModalDuplicateAdToAdGroup;
